import React, { useState, useRef, useEffect } from 'react';
import { Eraser, Upload, Download } from 'lucide-react';
import './ToolsCommon.css';

const BackgroundRemover = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [threshold, setThreshold] = useState(128);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setOriginalImage(event.target.result);
        processImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const processImage = (imageSrc) => {

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Simple background removal: Make similar colors transparent
      // This is a very basic algorithm. Production apps use AI models like rembg
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Calculate brightness
        const brightness = (r + g + b) / 3;

        // If pixel is close to white/light background, make it transparent
        if (brightness > threshold) {
          data[i + 3] = 0; // Set alpha to 0 (transparent)
        }
      }

      ctx.putImageData(imageData, 0, 0);
      setProcessedImage(canvas.toDataURL('image/png'));
    };
    img.src = imageSrc;
  };

  const downloadImage = () => {
    const link = document.createElement('a');
    link.download = 'no-background.png';
    link.href = processedImage;
    link.click();
  };

  useEffect(() => {
    if (originalImage) {
      processImage(originalImage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold]);

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <Eraser size={48} className="tool-icon" />
          <h1 className="tool-title">Background Remover</h1>
          <p className="tool-subtitle">Remove background from images</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="tool-button primary"
              style={{ width: '100%' }}
            >
              <Upload size={20} />
              Select Image
            </button>
          </div>

          {originalImage && (
            <>
              <div className="tool-section">
                <label className="tool-label">Sensitivity: {threshold}</label>
                <input
                  type="range"
                  min="50"
                  max="255"
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                  className="tool-range"
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                  Adjust to remove more or less background
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
                <div>
                  <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Original
                  </h3>
                  <div style={{ background: 'repeating-conic-gradient(#e5e7eb 0% 25%, white 0% 50%) 50% / 20px 20px', borderRadius: '12px', padding: '8px' }}>
                    <img src={originalImage} alt="Original" style={{ width: '100%', display: 'block', borderRadius: '8px' }} />
                  </div>
                </div>
                <div>
                  <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Background Removed
                  </h3>
                  <div style={{ background: 'repeating-conic-gradient(#e5e7eb 0% 25%, white 0% 50%) 50% / 20px 20px', borderRadius: '12px', padding: '8px' }}>
                    {processedImage && (
                      <img src={processedImage} alt="Processed" style={{ width: '100%', display: 'block', borderRadius: '8px' }} />
                    )}
                  </div>
                </div>
              </div>

              <button onClick={downloadImage} className="tool-button primary" style={{ width: '100%', marginTop: '24px' }}>
                <Download size={20} />
                Download PNG (Transparent)
              </button>

              <div style={{ marginTop: '16px', padding: '16px', background: '#dbeafe', borderRadius: '12px', fontSize: '14px', color: '#1e40af' }}>
                <strong>Tip:</strong> This uses a simple brightness-based algorithm. For better results with complex backgrounds, use AI-powered tools or adjust the sensitivity slider.
              </div>
            </>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );
};

export default BackgroundRemover;
